import React, { useState } from 'react';

import { Check, Copy, Key } from 'lucide-react';

import {
	useCreateAPIKeyMutation,
	useListAPIKeysQuery,
	useRevealAPIKeyMutation,
	useRevokeAPIKeyMutation,
} from '../../api/apiKeys';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../providers/AppAuthProvider';
import {
	Alert,
	Button,
	ConfirmBox,
	Input,
	Modal,
	ModalBody,
	ModalFooter,
	SkeletonTable,
	Typography,
} from '../common';
import { btn, card, SettingsPageHeader, td, th } from '../settings/settingsV9';

import type { APIKey } from '../../api/apiKeys';

interface APIKeysTabProps {
	enterpriseId?: string;
}

const formatDate = (iso: string) =>
	new Date(iso).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});

const APIKeysTab: React.FC<APIKeysTabProps> = ({ enterpriseId }) => {
	const { isAdmin, user } = useAuth();
	const { data, isLoading } = useListAPIKeysQuery(
		enterpriseId ? { enterpriseId: enterpriseId } : undefined,
	);
	const [createAPIKey, { isLoading: isCreating }] = useCreateAPIKeyMutation();
	const [revokeAPIKey, { isLoading: isRevoking }] = useRevokeAPIKeyMutation();
	const [revealAPIKey] = useRevealAPIKeyMutation();
	const { showToast } = useToast();

	const [showCreateModal, setShowCreateModal] = useState(false);
	const [newKeyName, setNewKeyName] = useState('');
	const [createdKey, setCreatedKey] = useState<APIKey | null>(null);
	const [revokingKeyId, setRevokingKeyId] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);

	const handleCreate = async () => {
		if (!newKeyName.trim()) {
			showToast('Error', 'Key name is required', 'error');
			return;
		}

		if (!enterpriseId) {
			showToast(
				'Error',
				'Organization ID is required to create a key',
				'error',
			);
			return;
		}

		try {
			const response = await createAPIKey({
				key_name: newKeyName,
				enterprise_id: enterpriseId,
			}).unwrap();
			setCreatedKey(response.api_key);
			showToast('Success', 'API key created successfully', 'success');
		} catch (error: any) {
			showToast(
				'Error',
				error?.data?.message || 'Failed to create API key',
				'error',
			);
		}
	};

	const handleRevoke = async (keyId: string) => {
		setRevokingKeyId(keyId);
	};

	const confirmRevoke = async () => {
		if (!revokingKeyId) return;
		try {
			await revokeAPIKey({ keyId: revokingKeyId, enterpriseId }).unwrap();
			showToast('Success', 'API key revoked', 'success');
		} catch (error: any) {
			showToast(
				'Error',
				error?.data?.message || 'Failed to revoke API key',
				'error',
			);
		} finally {
			setRevokingKeyId(null);
		}
	};

	const handleCopyKey = async (keyId: string) => {
		try {
			const response = await revealAPIKey({ keyId, enterpriseId }).unwrap();
			await navigator.clipboard.writeText(response.full_key);
			showToast('Success', 'Key copied to clipboard', 'success');
		} catch (error: any) {
			showToast(
				'Error',
				error?.data?.message || 'This key cannot be revealed.',
				'error',
			);
		}
	};

	const copyToClipboard = (text: string) => {
		navigator.clipboard
			.writeText(text)
			.then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			})
			.catch((err) => {
				console.error('Failed to copy text:', err);
				showToast('Error', 'Could not copy to clipboard.', 'error');
			});
	};

	if (isLoading) {
		return <SkeletonTable rows={5} columns={5} />;
	}

	const apiKeys = data?.api_keys || [];

	return (
		<div className="space-y-4">
			<SettingsPageHeader
				title="API Keys"
				subtitle="Keys for the pricing configuration API and the statement API"
				actions={
					isAdmin && (
						<button
							className={btn.primary}
							onClick={() => {
								setCreatedKey(null);
								setNewKeyName('');
								setShowCreateModal(true);
							}}
						>
							<Key className="h-4 w-4" />
							Create API Key
						</button>
					)
				}
			/>

			<div className={`${card} overflow-hidden`}>
				<div className="overflow-x-auto">
					<table className="w-full border-collapse">
						<thead>
							<tr>
								{[
									'Name',
									'Key',
									'Scope',
									'Created',
									'Last used',
									'Actions',
								].map((heading) => (
									<th key={heading} className={th}>
										{heading}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{apiKeys.map((key) => (
								<tr key={key.id}>
									<td className={`${td} font-medium text-primary-700`}>
										{key.key_name}
									</td>
									<td className={`${td} font-mono text-xs`}>
										{key.key_prefix}••••••••
									</td>
									{/* Keys are not scoped yet: one key reaches every API of the tenant. */}
									<td className={td}>All APIs</td>
									<td className={`${td} text-gray-500`}>
										{formatDate(key.created_at)}
									</td>
									<td className={`${td} text-gray-500`}>
										{key.last_used_at ? formatDate(key.last_used_at) : 'Never'}
									</td>
									<td className={td}>
										{key.is_active ? (
											isAdmin && (
												<span className="flex items-center gap-2">
													<button
														className={btn.secondarySm}
														onClick={() => handleCopyKey(key.id)}
													>
														<Copy className="h-4 w-4" />
														Copy
													</button>
													<button
														className={`${btn.secondarySm} !text-red-700`}
														onClick={() => handleRevoke(key.id)}
													>
														Revoke
													</button>
												</span>
											)
										) : (
											<span className="text-gray-500">Revoked</span>
										)}
									</td>
								</tr>
							))}
							{apiKeys.length === 0 && (
								<tr>
									<td colSpan={6} className={`${td} text-gray-500`}>
										{isAdmin
											? 'No API keys yet. Create one to call the pricing configuration API or the statement API.'
											: 'No active API keys found for this organization.'}
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>

			<Modal
				isOpen={showCreateModal}
				onClose={() => setShowCreateModal(false)}
				title={createdKey ? 'API Key Created' : 'Create New API Key'}
				size="md"
			>
				<ModalBody>
					{!createdKey ? (
						<div className="space-y-4">
							<Typography variant="body2" className="text-gray-600">
								Provide a descriptive name for your API key. This will help you
								identify its purpose later.
							</Typography>
							<div>
								<Typography
									variant="caption"
									className="mb-1 block font-medium"
								>
									Key Name
								</Typography>
								<Input
									value={newKeyName}
									onChange={(e) => setNewKeyName(e.target.value)}
									placeholder="e.g. CI/CD Integration, Marketing Service"
									fullWidth
									autoFocus
								/>
							</div>

							{user?.tenant_id && (
								<div>
									<Typography
										variant="caption"
										className="mb-1 block font-medium text-gray-700"
									>
										Your Tenant ID
									</Typography>
									<div className="bg-gray-50 p-2 rounded border border-gray-200 font-mono text-xs text-gray-600">
										{user.tenant_id}
									</div>
									<Typography
										variant="caption"
										className="text-gray-500 mt-1 block"
									>
										This ID must be included in the{' '}
										<code className="bg-gray-100 px-1 rounded text-[10px]">
											X-Tenant-ID
										</code>{' '}
										header.
									</Typography>
								</div>
							)}
							<Alert variant="info" title="Rate Limiting">
								Default rate limit is 1,000 requests per hour. You can update
								this later by contacting support.
							</Alert>
						</div>
					) : (
						<div className="space-y-4">
							<Alert variant="warning" title="Save this key now!">
								For security reasons, we only show this key once. If you lose
								it, you will need to create a new one.
							</Alert>
							<div
								data-ph-mask
								className="ph-no-capture bg-gray-50 p-4 rounded-lg border border-gray-200 font-mono text-sm break-all relative group"
							>
								{createdKey.full_key}
								<button
									onClick={() => copyToClipboard(createdKey.full_key || '')}
									className="absolute right-2 top-2 p-2 bg-white rounded-md shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors"
									title="Copy to clipboard"
								>
									{copied ? (
										<Check className="w-4 h-4 text-green-500" />
									) : (
										<Copy className="w-4 h-4 text-gray-500" />
									)}
								</button>
							</div>

							{createdKey.tenant_id && (
								<div className="mt-2">
									<Typography
										variant="caption"
										className="mb-1 block font-medium text-gray-700"
									>
										Tenant ID (Required for header)
									</Typography>
									<div className="bg-gray-50 p-3 rounded-lg border border-gray-200 font-mono text-sm break-all flex items-center justify-between">
										<span>{createdKey.tenant_id}</span>
										<button
											onClick={() => {
												navigator.clipboard
													.writeText(createdKey.tenant_id || '')
													.then(() =>
														showToast('Success', 'Tenant ID copied', 'success'),
													)
													.catch((err) => {
														console.error('Failed to copy Tenant ID:', err);
														showToast(
															'Error',
															'Could not copy Tenant ID.',
															'error',
														);
													});
											}}
											className="p-1.5 bg-white rounded-md shadow-sm border border-gray-200 hover:bg-gray-50 transition-colors ml-2"
											title="Copy Tenant ID"
										>
											<Copy className="w-3 h-3 text-gray-500" />
										</button>
									</div>
								</div>
							)}

							<Typography
								variant="caption"
								className="text-gray-500 text-center block italic"
							>
								Use this key in the{' '}
								<code className="bg-gray-100 px-1 rounded">X-API-Key</code>{' '}
								header of your requests.
							</Typography>
						</div>
					)}
				</ModalBody>
				<ModalFooter>
					{!createdKey ? (
						<>
							<Button
								onClick={() => setShowCreateModal(false)}
								variant="secondary"
							>
								Cancel
							</Button>
							<Button
								onClick={handleCreate}
								variant="primary"
								loading={isCreating}
							>
								Generate Key
							</Button>
						</>
					) : (
						<Button
							onClick={() => setShowCreateModal(false)}
							variant="primary"
							fullWidth
						>
							I've saved the key
						</Button>
					)}
				</ModalFooter>
			</Modal>

			{/* Revoke Confirmation Modal */}
			<ConfirmBox
				isOpen={!!revokingKeyId}
				onClose={() => setRevokingKeyId(null)}
				onConfirm={confirmRevoke}
				title="Revoke API Key"
				description="Are you sure you want to revoke this API key? This will immediately disable it and any applications using it will no longer be able to authenticate. This action cannot be undone."
				confirmText="Revoke Key"
				variant="danger"
				isLoading={isRevoking}
			/>
		</div>
	);
};

export default APIKeysTab;
